import { useState } from "react"
import { cn } from "@/lib/utils"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Crown, ChevronDown, Check, X } from "lucide-react"
import { useAppSelector } from "@/app/store/hooks"
import { useGetCartCreditStatusQuery } from "@/app/store/api/cartApi"

interface WorkspaceDecksBadgeProps {
    onClick?: () => void
}

interface Feature {
    label: string
    available: boolean
}

const FeatureRow = ({ label, available }: Feature) => (
    <div className="flex items-center gap-1.5">
        {available
            ? <Check className="h-3 w-3 text-emerald-500 flex-shrink-0" strokeWidth={2.5} />
            : <X className="h-3 w-3 text-red-400 flex-shrink-0" strokeWidth={2.5} />
        }
        <span className="text-[12px] text-blue-600 whitespace-nowrap">{label}</span>
    </div>
)

const CreditTile = ({ count, label, features, tileBg, tileText, tileHover }: {
    count: number; label: string; features: Feature[]
    tileBg: string; tileText: string; tileHover: string
}) => (
    <div className="relative group/tile">
        <div className={cn("flex items-center justify-center w-[52px] h-[52px] rounded-xl transition-colors cursor-default select-none", tileBg, tileHover)}>
            <span className={cn("text-[15px] font-bold", tileText)}>x{count}</span>
        </div>
        {/* Tooltip */}
        <div className="absolute top-full left-0 mt-2 hidden group-hover/tile:block z-50">
            <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-3 min-w-[148px]">
                <p className={cn("text-[13px] font-semibold mb-2", tileText)}>{label}</p>
                {features.length > 0 && (
                    <div className="flex flex-col gap-1.5">
                        {features.map(f => <FeatureRow key={f.label} {...f} />)}
                    </div>
                )}
            </div>
        </div>
    </div>
)

export function WorkspaceCreditsBadge({ onClick }: WorkspaceDecksBadgeProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [isInlineExpanded, setIsInlineExpanded] = useState(false);
    const { isAuthenticated } = useAppSelector((state) => state.auth);
    const selectedCurrency = useAppSelector((state) => state.currency.selectedCurrency);
    const { data: statusData } = useGetCartCreditStatusQuery(selectedCurrency, { skip: !isAuthenticated });

    const isExpanded = isOpen || isInlineExpanded;

    const credits = statusData?.data?.credits;

    // ── Free credits (from the backend's freeRemaining per product) ──
    const freeProRemaining       = isAuthenticated ? (credits?.proDecks?.freeRemaining       ?? 1) : 1;
    const freeFastRemaining      = isAuthenticated ? (credits?.fastDecks?.freeRemaining      ?? 1) : 1;
    const freePitchRemaining     = isAuthenticated ? (credits?.fundraisingDecks?.freeRemaining ?? 1) : 1;
    const freeDocsRemaining      = isAuthenticated ? (credits?.documents?.freeRemaining      ?? 1) : 1;
    const freeResearchRemaining  = isAuthenticated ? (credits?.marketResearch?.freeRemaining  ?? 1) : 1;

    // ── Purchased / paid credits ──────────────────────────────────────
    const purchasedPro       = (credits?.proDecks?.purchased       ?? 0) - (credits?.proDecks?.used ?? 0);
    const purchasedFast      = (credits?.fastDecks?.purchased      ?? 0) - (credits?.fastDecks?.used ?? 0);
    const purchasedPitch     = (credits?.fundraisingDecks?.purchased ?? 0) - (credits?.fundraisingDecks?.used ?? 0);
    const purchasedDocs      = (credits?.documents?.purchased      ?? 0) - (credits?.documents?.used ?? 0);
    const purchasedResearch  = (credits?.marketResearch?.purchased  ?? 0) - (credits?.marketResearch?.used ?? 0);

    // ── Total remaining for the signal bar ───────────────────────────
    const totalProRemaining      = freeProRemaining + purchasedPro;
    const totalFastRemaining     = freeFastRemaining + purchasedFast;
    const totalPitchRemaining    = freePitchRemaining + purchasedPitch;
    const totalDocsRemaining     = freeDocsRemaining + purchasedDocs;
    const totalResearchRemaining = freeResearchRemaining + purchasedResearch;
    const totalItemsRemaining    = totalProRemaining + totalFastRemaining + totalPitchRemaining + totalDocsRemaining + totalResearchRemaining;

    // Signal bar: based on total remaining vs a rough max (free + purchased)
    const totalProCapacity    = (credits?.proDecks?.freeTotal ?? 1) + (credits?.proDecks?.purchased ?? 0);
    const totalFastCapacity   = (credits?.fastDecks?.freeTotal ?? 1) + (credits?.fastDecks?.purchased ?? 0);
    const totalPitchCapacity  = (credits?.fundraisingDecks?.freeTotal ?? 1) + (credits?.fundraisingDecks?.purchased ?? 0);
    const totalDocsCapacity   = (credits?.documents?.freeTotal ?? 1) + (credits?.documents?.purchased ?? 0);
    const totalResearchCapacity = (credits?.marketResearch?.freeTotal ?? 1) + (credits?.marketResearch?.purchased ?? 0);
    const totalCapacity       = totalProCapacity + totalFastCapacity + totalPitchCapacity + totalDocsCapacity + totalResearchCapacity;

    const level = Math.max(0, Math.min(1, totalItemsRemaining / Math.max(1, totalCapacity)));

    let color = "bg-emerald-500"
    if (level < 0.5) color = "bg-amber-500"
    if (level < 0.25) color = "bg-red-500"

    const bars = 4
    const activeBars = Math.ceil(level * bars)

    const allFeatures: Feature[] = [
        { label: "Generation", available: true },
        { label: "Download", available: true },
        { label: "Share & Analytics", available: true },
    ]

    const addonFeatures: Feature[] = [
        { label: "Generation", available: true },
        { label: "Download", available: true },
    ]

    return (
        <div
            className={cn(
                "flex items-center h-8.5 rounded-xl overflow-hidden",
                "border border-[#E6D3A3]",
                "bg-[#F8F3E8]",
                "transition-all duration-200",
            )}
        >
            <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger asChild>
                    <div className="flex items-center gap-3 px-3.5 h-full cursor-pointer hover:bg-slate-100/50 transition-colors group">
                        <div className="flex items-end gap-[2px]">
                            {[...Array(bars)].map((_, i) => (
                                <div
                                    key={i}
                                    className={cn("w-[3px] transition-colors rounded-t-sm", i < activeBars ? color : "bg-[#D9C8A1]")}
                                    style={{ height: `10px` }}
                                />
                            ))}
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="text-xs font-semibold text-[#3A2E18] tracking-tight whitespace-nowrap transition-all duration-200">
                                {isAuthenticated ? (
                                    isExpanded ? (
                                        <>
                                            {`${totalItemsRemaining} ${totalItemsRemaining === 1 ? 'Deck' : 'Decks'}`}
                                            <span
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsInlineExpanded(false);
                                                }}
                                                className="cursor-pointer hover:opacity-75 transition-opacity"
                                                title="Click to collapse"
                                            >
                                                {" Remaining"}
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            {`${totalItemsRemaining} ${totalItemsRemaining === 1 ? 'Deck' : 'Decks'}`}
                                            <span
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsInlineExpanded(true);
                                                }}
                                                className="cursor-pointer opacity-70 hover:opacity-100 font-bold transition-opacity ml-0.5"
                                                title="Click to expand"
                                            >
                                                ..
                                            </span>
                                        </>
                                    )
                                ) : (
                                    isExpanded ? (
                                        <>
                                            {"5 Free Decks"}
                                            <span
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsInlineExpanded(false);
                                                }}
                                                className="cursor-pointer hover:opacity-75 transition-opacity"
                                                title="Click to collapse"
                                            >
                                                {" Remaining"}
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            {"5 Free Decks"}
                                            <span
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsInlineExpanded(true);
                                                }}
                                                className="cursor-pointer opacity-70 hover:opacity-100 font-bold transition-opacity ml-0.5"
                                                title="Click to expand"
                                            >
                                                ..
                                            </span>
                                        </>
                                    )
                                )}
                            </span>
                            <ChevronDown className="h-3 w-3 text-[#3A2E18] opacity-50 group-data-[state=open]:rotate-180 transition-transform" />
                        </div>
                    </div>
                </PopoverTrigger>

                <PopoverContent
                    className="w-auto p-2 bg-slate-100 border border-slate-200/60 rounded-2xl shadow-2xl shadow-slate-300/40 overflow-visible"
                    sideOffset={8}
                    align="end"
                >
                    <div className="flex items-stretch gap-2">

                        {/* ── FREE ── */}
                        <div className="flex flex-col gap-3 bg-white rounded-xl px-3 pt-3 pb-3.5 border border-slate-100">
                            <div className="flex items-center gap-2">
                                <span className="text-[13px] font-bold text-blue-600">Free</span>
                                {(freeProRemaining + freeFastRemaining + freePitchRemaining + freeDocsRemaining + freeResearchRemaining) === 0 && (
                                    <span className="text-[10px] bg-red-100/80 text-red-600 px-1.5 py-0.5 rounded-md font-bold uppercase tracking-wider">Exhausted</span>
                                )}
                            </div>
                            <div className="flex gap-1.5">
                                <CreditTile count={freeProRemaining}      label="Pro Deck"    features={allFeatures} tileBg="bg-blue-100/60" tileHover="hover:bg-blue-100" tileText="text-blue-600" />
                                <CreditTile count={freeFastRemaining}     label="Fast Deck"   features={allFeatures} tileBg="bg-blue-100/60" tileHover="hover:bg-blue-100" tileText="text-blue-600" />
                                <CreditTile count={freePitchRemaining}    label="Pitch Deck"  features={allFeatures} tileBg="bg-blue-100/60" tileHover="hover:bg-blue-100" tileText="text-blue-600" />
                                <CreditTile count={freeDocsRemaining}     label="Document"    features={allFeatures} tileBg="bg-blue-100/60" tileHover="hover:bg-blue-100" tileText="text-blue-600" />
                                <CreditTile count={freeResearchRemaining} label="Research"    features={allFeatures} tileBg="bg-blue-100/60" tileHover="hover:bg-blue-100" tileText="text-blue-600" />
                            </div>
                        </div>

                        {/* ── PURCHASED ── (only show if any purchased credits exist) */}
                        {(purchasedPro + purchasedFast + purchasedPitch + purchasedDocs + purchasedResearch) > 0 && (
                            <div className="flex flex-col gap-3 bg-white rounded-xl px-3 pt-3 pb-3.5 border border-slate-100">
                                <div className="flex items-center gap-2">
                                    <span className="text-[13px] font-bold text-blue-600">Purchased</span>
                                    <span className="text-[10px] bg-emerald-100 text-emerald-600 px-1.5 py-0.5 rounded-md font-bold uppercase tracking-wider">Paid</span>
                                </div>
                                <div className="flex gap-1.5">
                                    {purchasedPro > 0      && <CreditTile count={purchasedPro}      label="Pro Deck"   features={addonFeatures} tileBg="bg-emerald-100/60" tileHover="hover:bg-emerald-100" tileText="text-emerald-600" />}
                                    {purchasedFast > 0     && <CreditTile count={purchasedFast}     label="Fast Deck"  features={addonFeatures} tileBg="bg-emerald-100/60" tileHover="hover:bg-emerald-100" tileText="text-emerald-600" />}
                                    {purchasedPitch > 0    && <CreditTile count={purchasedPitch}    label="Pitch Deck" features={addonFeatures} tileBg="bg-emerald-100/60" tileHover="hover:bg-emerald-100" tileText="text-emerald-600" />}
                                    {purchasedDocs > 0     && <CreditTile count={purchasedDocs}     label="Document"   features={addonFeatures} tileBg="bg-emerald-100/60" tileHover="hover:bg-emerald-100" tileText="text-emerald-600" />}
                                    {purchasedResearch > 0 && <CreditTile count={purchasedResearch} label="Research"   features={addonFeatures} tileBg="bg-emerald-100/60" tileHover="hover:bg-emerald-100" tileText="text-emerald-600" />}
                                </div>
                            </div>
                        )}

                    </div>
                </PopoverContent>
            </Popover>

            {isAuthenticated && (
                <>
                    <div className="w-px h-full bg-[#E6D3A3]" />

                    <div onClick={onClick} className="flex flex-shrink-0 cursor-pointer items-center gap-1.5 px-3.5 h-full
                        bg-gradient-to-r from-amber-200 to-yellow-300
                        text-[#2A1D00] font-semibold text-xs
                        hover:from-amber-300 hover:to-yellow-400
                        transition-all">
                        <Crown className="w-3.5 h-3.5" fill="currentColor" />
                        Buy more
                    </div>
                </>
            )}
        </div>
    )
}
